import { Injectable } from '@angular/core';
import {
  GetMultiOrderFormsResponseOrderFormContainerInterface as MPOrderFormContainerInterface,
  OrderForm,
} from '@galaxy/marketplace-apps/v1';
import {
  CheckboxField,
  CommonFormData,
  DropdownField,
  FieldBase,
  FileUploadGroupField,
  ResellerItem,
  TextareaField,
  TextboxField,
  TextBoxFieldOptions,
  VBCUserField,
  VBCUserFieldOptions,
} from '@vendasta/store';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account } from '../../core/account';
import { AccountService } from '../../core/account.service';
import { Salesperson } from '../../core/salesperson';
import { User } from '../../core/user';
import { FILE_UPLOAD_URL } from './constants';
import { CommonFormSection, ResellerItemOrderForm } from './interface';

export interface Activation {
  productId: string;
  name: string;
  isSuccess?: boolean;
  errorMessage?: string;
}

@Injectable()
export class ProductOrderFormService {
  constructor(private accountService: AccountService) {}

  static commonFieldsMap = {
    contact_email: 'Contact Email',
    contact_name: 'Contact Name',
    contact_phone_number: 'Contact Phone Number',
    business_account_group_id: 'Business Account Group ID',
    business_name: 'Business Name',
    business_address: 'Business Address',
    business_phone_number: 'Business Phone Number',
    business_website: 'Business Website',
    salesperson_name: 'Salesperson Name',
    salesperson_email: 'Salesperson Email',
    salesperson_phone_number: 'Salesperson Phone Number',
  };

  static allCommonFieldsMap = [
    'contact_email',
    'contact_name',
    'contact_phone_number',
    'business_account_group_id',
    'business_name',
    'business_address',
    'business_phone_number',
    'business_website',
    'salesperson_name',
    'salesperson_email',
    'salesperson_phone_number',
  ];
  private fileUploadUrl = FILE_UPLOAD_URL;
  productIds: string[];
  subscriptions: Subscription[] = [];

  // We don't currently have a string data contract between vendor center and partner center in terms of forms fields.
  fieldTypeMap = {
    text: TextboxField,
    textbox: TextboxField,
    dropdown: DropdownField,
    checkbox: CheckboxField,
    file: FileUploadGroupField,
    textarea: TextareaField,
    vbcuser: VBCUserField,
  };

  static buildCustomEntryUrlForOrderForm(productID: string): TextBoxFieldOptions {
    return {
      id: 'custom-entry-url-' + productID,
      label: 'Custom Entry Url',
      type: 'text',
      required: true,
      regexValidator: '^(http://|https://).*',
      regexErrorMessage: 'Not in correct format. Should be prefixed with https or http.',
      textboxType: 'text',
    } as TextBoxFieldOptions;
  }

  static getFieldIds(commonFormSections: CommonFormSection[]): string[] {
    return commonFormSections.reduce((accumulator, section) => {
      accumulator.push(...section.fields.map((field) => field.id));
      return accumulator;
    }, []);
  }

  static getFirstCreatedUser(users: User[]): User {
    return users.reduce((first, current) => {
      return first && first.createdDateTime < current.createdDateTime ? first : current;
    }, null);
  }

  static extractCommonFieldData(account: Account, salesperson: Salesperson, user: User): CommonFormData {
    return {
      contact_email: user ? user.email : '',
      contact_name: user ? user.name : '',
      contact_phone_number: user ? user.phoneNumber : '',
      business_account_group_id: account ? account.account_group_id : '',
      business_name: account ? account.company_name : '',
      business_address: account ? account.fullAddress : '',
      business_phone_number: account && account.work_number && account.work_number.length ? account.work_number[0] : '',
      business_website: account ? account.website : '',
      salesperson_name: salesperson ? salesperson.name : '',
      salesperson_email: salesperson ? salesperson.email : '',
      salesperson_phone_number: salesperson ? salesperson.contactNumber : '',
    };
  }

  static pickActiveFieldData(activeFieldIds: string[], fieldData: Record<string, unknown>): Record<string, unknown> {
    return Object.keys(fieldData).reduce((accumulator, fieldId) => {
      if (activeFieldIds.indexOf(fieldId) >= 0) {
        accumulator[fieldId] = fieldData[fieldId];
      }
      return accumulator;
    }, {});
  }

  createResellerItem(
    resellerItem: ResellerItem,
    users: User[],
    orderForm: MPOrderFormContainerInterface,
  ): ResellerItemOrderForm {
    const resellerOrderForm = new ResellerItemOrderForm({ product: resellerItem });
    let entryUrlField = [];
    let customField = [];
    if (resellerItem.usesActivationSpecificEntryUrl) {
      entryUrlField = [new TextboxField(ProductOrderFormService.buildCustomEntryUrlForOrderForm(resellerItem.appId))];
    }

    if (resellerItem.usesCustomizationForm) {
      customField = this.buildCustomOrderFormFields(orderForm, users || []);
      resellerOrderForm.commonFields = this.buildCommonOrderFormFields(resellerItem);
    }
    resellerOrderForm.customFields = entryUrlField.concat(customField);
    resellerOrderForm.activationMessage = resellerItem.orderForm.activationMessage;
    if (orderForm) {
      resellerOrderForm.orderForm = new OrderForm(orderForm.orderForm);
    }
    return resellerOrderForm;
  }

  // extracts common field values from account data
  getCommonFieldValues(): Observable<CommonFormData> {
    return this.accountService.accountDetails$.pipe(
      map(({ account, users, salesperson }) => {
        const firstUser = users ? ProductOrderFormService.getFirstCreatedUser(users) : null;
        return ProductOrderFormService.extractCommonFieldData(account, salesperson, firstUser);
      }),
    );
  }

  getCustomProductEntryURl(purchasableOrderForm: ResellerItemOrderForm): string {
    let urlValue = '';
    const customEntryUrlField = purchasableOrderForm.customFields.find(
      (field) => field.id === 'custom-entry-url-' + purchasableOrderForm.appId,
    );
    if (customEntryUrlField) {
      urlValue = customEntryUrlField.value;
    }
    return urlValue;
  }

  buildCustomOrderFormFields(orderForm: MPOrderFormContainerInterface, users?: User[]): FieldBase<any>[] {
    if (
      !!orderForm &&
      orderForm.orderForm &&
      orderForm.orderForm.orderForm &&
      orderForm.orderForm.orderForm.length > 0
    ) {
      return orderForm.orderForm.orderForm
        .filter((field) => this.fieldTypeMap[field.type])
        .map((field) => {
          const fieldClass = this.fieldTypeMap[field.type];
          switch (fieldClass) {
            case CheckboxField: {
              const checkField = new fieldClass(field);
              checkField.required = false;
              return new fieldClass(checkField);
            }
            case DropdownField: {
              const dropDown = new fieldClass(field);
              if (field.options) {
                dropDown.options = field.options.map((option) => {
                  return { value: option, label: option };
                });
              }
              return dropDown;
            }
            case VBCUserField: {
              const fieldConfig = {
                description: field.description,
                id: field.id,
                required: field.required,
                type: field.type,
                label: field.label,
                users: users,
              } as VBCUserFieldOptions;
              return new fieldClass(fieldConfig);
            }
            case FileUploadGroupField:
              field = Object.assign({}, field); //Used to eliminate a pass by reference bug
              if (field.description) {
                field.description = field.description.concat('\n');
              }
              if (!(field.description === undefined || field.description === null)) {
                field.description = field.description.concat(
                  'Max 5 files. Zip common files together if more are needed.',
                );
              }
              field.uploadUrl = this.fileUploadUrl;
              return new fieldClass(field);

            default:
              return new fieldClass(field);
          }
        });
    }
    return [];
  }

  buildCommonOrderFormFields(_resellerItem: ResellerItem): CommonFormSection[] {
    // For a list of mat-icons, visit https://material.io/icons/
    const businessSection = { sectionName: 'Business', mdIconName: 'business', fields: [] };
    const salespersonSection = { sectionName: 'Salesperson', mdIconName: 'account_circle', fields: [] };
    const contactSection = { sectionName: 'Contact', mdIconName: 'account_circle', fields: [] };

    ProductOrderFormService.allCommonFieldsMap.map((field) => {
      const fieldLabel = ProductOrderFormService.commonFieldsMap[field];
      // Check if we have this field in our field map
      if (fieldLabel) {
        const textField = new TextboxField({ id: field, label: fieldLabel, textboxType: 'text' });
        if (field.startsWith('contact')) {
          contactSection.fields.push(textField);
        }
        if (field.startsWith('business')) {
          businessSection.fields.push(textField);
        }
        if (field.startsWith('salesperson')) {
          salespersonSection.fields.push(textField);
        }
      }
    });

    return [businessSection, contactSection, salespersonSection].filter((section) => section.fields.length > 0);
  }

  destroyService(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
